import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import examService from "../../services/ExamService";
import AppLoading from "../shared/loading/AppLoading";
import useDashboardBody from "./useDashboardBody";
import { toast } from "react-toastify";
import "./ExamineticsDashboardBody.scss";
import GLOBAL from "../../global";
import { useDispatch } from 'react-redux';
import { setOpenBiometricMovementAndCautionQuestionContainer } from "../../features/dashboard/dashboardSlice";
import BiometricMovementAndCautionQuestionContainer from "../shared/MovementModal/BiometricMovementAndCautionQuestionContainer";
import { useTenantFeatures } from "../../features/tenantFeatures/useTenantFeature";

const ExamineticsDashboardBody = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();
    const { notificationHandler } = useDashboardBody();
    const conformationAlertContentRef = useRef({});
    const location = useLocation();

    // Redux state for chatbot initialization and biometric modal
    const initChatBot = useSelector(state => state.chatBot?.chatBotUIState?.initChatBot);
    const dashboardRootState = useSelector(state => state.dashboard);
    const openBiometricModal = dashboardRootState?.biometric?.openBiometricMovementAndCautionQuestionContainer;
    const { isExamineticsEnabled } = useTenantFeatures();

    const prevStatesRef = useRef({ initChatBot, openBiometricModal });

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await examService.getExamineticsDashboard({});
            if (response?.success && response?.data) {
                setDashboardData(response.data);
            } else {
                toast.error(response?.error || 'Failed to load dashboard data');
            }
        } catch (error) {
            console.error(error);
            toast.error('An unexpected error occurred while fetching dashboard data.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [location, fetchData]);

    // Re-fetch when chatbot or biometric modal is closed
    useEffect(() => {
        const { initChatBot: prevInit, openBiometricModal: prevBio } = prevStatesRef.current;

        const chatbotClosed = prevInit && !initChatBot;
        const biometricModalClosed = prevBio && !openBiometricModal;

        if (chatbotClosed || biometricModalClosed) {
            fetchData();
        }

        prevStatesRef.current = { initChatBot, openBiometricModal };
    }, [initChatBot, openBiometricModal, fetchData]);

    const handleActionClick = (item, isNewRequest = false) => {
        // Prevent duplicate action: the framework prevents this generally, but we could add a loading local state if needed.
        // The mock ticket says "Prevent duplicate actions for same InvitationId".
        // `useDashboardBody` handles this via Redux partially, but we will pass it directly to `notificationHandler`.
        if(item) {
            const notification = {
                ...item,
                IsLastIncomplete: !isNewRequest,
                InvitationTypeId: item.InvitationTypeId || 1 // Default to 1 (Self) for Examinetics screenings
            };
            notificationHandler(notification, conformationAlertContentRef);
        }
    };

    const handleSetOpenBiometricMovementAndCautionQuestionContainer = () => {
        dispatch(setOpenBiometricMovementAndCautionQuestionContainer({
            openBiometricMovementAndCautionQuestionContainer: false,
            testId: '',
            selectedBodyRegions: []
        }));
    }

    if (isLoading) {
        return (
            <div className="position-center">
                <AppLoading />
            </div>
        );
    }

    if (!dashboardData) {
        return null;
    }

    const { NewRequest, IncompleteRequest } = dashboardData;

    const showNewRequest = NewRequest?.IsHaveAnyElement;
    const showIncompleteRequest = IncompleteRequest?.IsHaveAnyElement;

    if (!showNewRequest && !showIncompleteRequest) {
        return (
            <div className="examinetics-dashboard-empty">
                <p>No screening requests available at this time.</p>
            </div>
        );
    }

    return (
        <div className="examinetics-dashboard-container">
            {showNewRequest && (
                <div className="examinetics-dashboard-section">
                    <h3 className="heading-large new-request-title">{NewRequest.Message}</h3>

                    {NewRequest.DashboardRequestList?.map((item, index) => (
                        <div key={item.InvitationId || index} className="examinetics-request-card">
                            {(!isExamineticsEnabled || item?.RequestedBy) && (
                                <p className="requested-by">Requested By: <span>{item.RequestedBy}</span></p>
                            )}
                            <p className="description-line">{NewRequest.DescriptionLine1}</p>
                            <p className="description-line">{NewRequest.DescriptionLine2}</p>
                            <button 
                                className="button button-dashboard examinetics-action-button"
                                onClick={() => handleActionClick(item, true)}
                            >
                                {NewRequest.ButtonName}
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {showIncompleteRequest && (
                <div className="examinetics-dashboard-section">
                    <h3 className="heading-large incomplete-request-title">{IncompleteRequest.Message}</h3>

                    {IncompleteRequest.DashboardRequestList?.map((item, index) => (
                        <div key={item.InvitationId || index} className="examinetics-request-card">
                            <h4 className="screening-name">{IncompleteRequest.ScreeningName}</h4>
                            <p className="status-line">Status: <span>{item.Status}</span></p>
                            <p className="description-line">{IncompleteRequest.DescriptionLine1}</p>
                            <p className="description-line">{IncompleteRequest.DescriptionLine2}</p>
                            <button 
                                className="button button-dashboard examinetics-action-button"
                                onClick={() => handleActionClick(item, false)}
                            >
                                {IncompleteRequest.ButtonName}
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {openBiometricModal &&
                <BiometricMovementAndCautionQuestionContainer
                    data={{
                        testId: dashboardRootState?.biometric?.testId,
                        selectedBodyRegions: { bodyRegionList: dashboardRootState?.biometric?.selectedBodyRegions || [] },
                        openBiometricMovementAndCautionQuestionContainer: openBiometricModal,
                    }}
                    setOpenBiometricMovementAndCautionQuestionContainer={handleSetOpenBiometricMovementAndCautionQuestionContainer}
                />
            }
        </div>
    );
};

export default ExamineticsDashboardBody;
 